// ============================================================
// ExerciseGuide.tsx — 矯正運動指引元件
// 提供可在工作崗位或休息時進行的姿勢矯正運動
// 每個運動附有步驟說明、計時器及可針對問題篩選
// ============================================================

import React, { FC, useState, useEffect, useRef } from 'react';
import { Exercise, PostureIssueType } from 'types/posture';

// ── 長者友善色彩常數（背景 rgb(10,10,26)） ────────────────
const C = {
  textPrimary:    '#FFFFFF',
  textSecondary:  '#C8D8E8',
  textMuted:      '#9AAFBF',
  textContent:    '#E0EAF0',
  accent:         '#60C8FF',
  accentBg:       'rgba(96,200,255,0.18)',
  success:        '#00FF88',
  successBg:      'rgba(0,255,136,0.15)',
  successText:    '#7FFFB0',
  warning:        '#FFD166',
  warningBg:      'rgba(255,209,102,0.15)',
  cardBg:         'rgba(255,255,255,0.07)',
  border:         'rgba(255,255,255,0.22)',
  borderActive:   '#60C8FF',
  stepActiveBg:   'rgba(96,200,255,0.18)',
  timerTrack:     'rgba(255,255,255,0.1)',
};

// ── 運動資料庫（不變） ───────────────────────────────────
const EXERCISES: Exercise[] = [
    {
        id:                  'chin_tuck',
        name:                '收下巴練習',
        nameJp:              'あご引き運動',
        targetIssue:         PostureIssueType.HEAD_FORWARD,
        duration:            '3 分鐘',
        sets:                3,
        reps:                10,
        canDoAtWorkstation:  true,            // ✅ 可在崗位上進行
        instructions: [
            '站立或坐直，視線保持水平向前',
            '在不點頭的情況下，將頭部水平向後平移（像「雙下巴」動作）',
            '感受後頸部有輕微拉伸感時停住',
            '保持此姿勢 5 秒鐘，感受頸部深層肌肉輕微出力',
            '緩慢回到起始位置，放鬆 2 秒',
            '重複 10 次為一組，共做 3 組',
        ],
    },
    {
        id:                  'shoulder_roll',
        name:                '肩膀繞環運動',
        nameJp:              '肩回し運動',
        targetIssue:         PostureIssueType.SHOULDER_UNEVEN,
        duration:            '2 分鐘',
        sets:                2,
        reps:                10,
        canDoAtWorkstation:  true,
        instructions: [
            '站立，雙臂自然垂於身側',
            '雙肩同時向前→向上→向後→向下做大圓圈繞環',
            '動作要慢而圓滑，充分活動肩胛骨',
            '完成 10 圈後，反方向再做 10 圈',
            '過程中感受肩膀的緊繃感逐漸釋放',
        ],
    },
    {
        id:                  'lateral_stretch',
        name:                '側腰伸展',
        nameJp:              '側屈ストレッチ',
        targetIssue:         PostureIssueType.SPINE_TILT,
        duration:            '2 分鐘',
        sets:                2,
        reps:                1,               // 每次保持 30 秒
        canDoAtWorkstation:  true,
        instructions: [
            '站立，雙腳與肩同寬',
            '右手臂舉過頭頂，身體向左側彎',
            '左手可沿左腿外側輕輕下滑引導方向',
            '感受右側腰部有明顯拉伸感，停留 30 秒',
            '緩慢回到正中位置，換另一側重複',
            '注意：側彎時保持骨盆固定，不要旋轉',
        ],
    },
    {
        id:                  'chest_stretch',
        name:                '胸部開展伸展',
        nameJp:              '胸部ストレッチ',
        targetIssue:         PostureIssueType.ROUNDED_BACK,
        duration:            '3 分鐘',
        sets:                3,
        reps:                1,
        canDoAtWorkstation:  false,           // ❌ 需要找牆壁或門框
        instructions: [
            '找一個門框或牆角',
            '雙手手肘彎曲 90°，前臂貼著門框兩側',
            '雙腳一前一後站立，身體緩慢向前傾',
            '感受胸口和前肩有明顯拉伸感時停住',
            '保持 30 秒，自然呼吸，不要憋氣',
            '感受每次呼氣時胸口都能再放鬆一點',
        ],
    },
    {
        id:                  'hip_level_exercise',
        name:                '骨盆中立練習',
        nameJp:              '骨盤中立運動',
        targetIssue:         PostureIssueType.PELVIC_TILT,
        duration:            '3 分鐘',
        sets:                3,
        reps:                10,
        canDoAtWorkstation:  true,
        instructions: [
            '站立，背部靠牆，腳跟距牆約 5cm',
            '注意腰部與牆之間的空隙（通常可以放入一隻手）',
            '收緊腹肌，同時輕微夾緊臀部，將腰部空隙縮小',
            '感受骨盆向後微傾（「骨盆後傾」）的感覺',
            '保持這個感覺 5 秒後放鬆',
            '重複 10 次，培養骨盆中立的肌肉記憶',
        ],
    },
    {
        id:                  'knee_release',
        name:                '小腿提踵循環',
        nameJp:              'カーフレイズ',
        targetIssue:         PostureIssueType.KNEE_LOCKED,
        duration:            '2 分鐘',
        sets:                2,
        reps:                20,
        canDoAtWorkstation:  true,
        instructions: [
            '站立，雙腳與肩同寬，可輕扶台面保持平衡',
            '緩慢踮起腳尖，盡量將腳跟抬高',
            '在最高點停留 1 秒，感受小腿肌肉收縮',
            '緩慢放下腳跟（不要用力「踩」地板）',
            '重複 20 次，動作要慢而穩定',
            '此運動同時促進下肢靜脈回流，預防久站水腫',
        ],
    },
    {
        id:                  'weight_distribution',
        name:                '體重均衡站立訓練',
        nameJp:              '体重均等立ち訓練',
        targetIssue:         PostureIssueType.WEIGHT_SHIFT,
        duration:            '1 分鐘',
        sets:                1,
        reps:                1,
        canDoAtWorkstation:  true,
        instructions: [
            '站立，雙腳與肩同寬，輕輕閉上眼睛',
            '感受雙腳底板：腳跟、大拇指根部、小拇指根部各三點的受力',
            '有意識地調整重心，使左右腳受力相等',
            '想像自己站在兩個體重計上，兩側顯示相同數字',
            '保持這個均衡感 30 秒，培養本體感覺',
            '睜開眼睛，嘗試維持同樣的重心均衡感',
        ],
    },
];

// ── 計時器 Hook ─────────────────────────────────────────
function useTimer(totalSeconds: number) {
    const [remaining, setRemaining] = useState<number>(totalSeconds);
    const [isRunning, setIsRunning] = useState<boolean>(false);
    const intervalRef               = useRef<ReturnType<typeof setInterval> | null>(null);

    // 啟動計時器
    function start(): void {
        if (isRunning) return;
        setIsRunning(true);
        intervalRef.current = setInterval(() => {
            setRemaining(prev => {
                if (prev <= 1) {
                    clearInterval(intervalRef.current!);
                    setIsRunning(false);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    }

    // 暫停
    function pause(): void {
        if (intervalRef.current) clearInterval(intervalRef.current);
        setIsRunning(false);
    }

    // 重設
    function reset(): void {
        pause();
        setRemaining(totalSeconds);
    }

    // 元件卸載時清除 interval
    useEffect(() => {
        return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
    }, []);

    const minutes = Math.floor(remaining / 60);
    const seconds = remaining % 60;
    const display = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    const progress = ((totalSeconds - remaining) / totalSeconds) * 100;

    return { remaining, isRunning, display, progress, start, pause, reset };
}

// ── 單一運動卡片元件 ─────────────────────────────────────
interface ExerciseCardProps {
    exercise: Exercise;
}

const ExerciseCard: FC<ExerciseCardProps> = ({ exercise }) => {
    const [isExpanded, setIsExpanded] = useState<boolean>(false);
    const [currentStep, setCurrentStep] = useState<number>(0);

    // 計時器（30 秒為預設一組計時）
    const timer = useTimer(30);

    // 問題類型中文標籤
    const issueLabel: Record<PostureIssueType, string> = {
        HEAD_FORWARD:    '頭部前傾',
        SHOULDER_UNEVEN: '肩膀不水平',
        SPINE_TILT:      '脊椎側傾',
        ROUNDED_BACK:    '圓背',
        PELVIC_TILT:     '骨盆傾斜',
        KNEE_LOCKED:     '膝蓋鎖死',
        WEIGHT_SHIFT:    '重心偏移',
    };

  const radius        = 34;
  const circumference = 2 * Math.PI * radius;

    return (
        <div style={{
      background:   C.cardBg,
      border:       `2px solid ${isExpanded ? C.borderActive : C.border}`,
      borderRadius: '16px',
      marginBottom: '16px',
            overflow:     'hidden',
            transition:   'border-color 0.2s',
        }}>
            {/* ── 卡片標題列 ─────────────────────────────── */}
            <div
                onClick={() => setIsExpanded(prev => !prev)}
                style={{
                    display:    'flex',
                    alignItems: 'center',
          padding:    '20px',             // ↑ 原本 16px
                    cursor:     'pointer',
          gap:        14,
                }}
            >
                {/* 運動圖示 */}
                <div style={{
                    width:        44, height: 44,
                    borderRadius: '10px',
                    background:   'rgba(0,170,255,0.15)',
                    display:      'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize:     32, flexShrink: 0,
                }}>
                    {exercise.canDoAtWorkstation ? '🏪' : '🏃'}
                </div>

                <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <h4 style={{
                            color:      C.textPrimary,
                            fontSize:   18,             // ↑ 原本未設定（繼承約14）
                            fontWeight: 700,
                        }}>
                            {exercise.name}
                        </h4>
                        {exercise.canDoAtWorkstation && (
                            <span style={{
                background:   C.successBg,
                border:       `1px solid ${C.success}`,
                borderRadius: '20px',
                color:        C.success,
                fontSize:     12,         // ↑ 原本未設定
                fontWeight:   600,
                padding:      '3px 10px',
                            }}>
                崗位可做
              </span>
                        )}
                    </div>
                    <p style={{
                        color:     C.textSecondary,
                        fontSize:  14,               // ↑ 原本未設定（約12）
                        marginTop: 4,
                        lineHeight: 1.5,
                    }}>
                        {exercise.nameJp} ｜ 針對：{issueLabel[exercise.targetIssue]} ｜ {exercise.duration}
                    </p>
                </div>

                {/* 展開/收合箭頭 */}
                <span style={{
                    color:      C.textMuted,
                    fontSize:   20,
                    transform:  isExpanded ? 'rotate(180deg)' : 'none',
                    transition: 'transform 0.2s',
                }}>
          ▼
        </span>
            </div>

            {/* ── 展開內容 ───────────────────────────────── */}
            {isExpanded && (
                <div style={{ padding: '0 20px 24px', borderTop: '1px solid #1a1a2e' }}>

                    {/* 訓練參數 */}
                    <div style={{
            display:      'flex',
            gap:          12,
            marginBottom: 20,
            flexWrap:     'wrap',
                    }}>
            {[
              { icon: '🔄', label: `${exercise.sets} 組` },
              { icon: '✖️', label: `${exercise.reps} 次/組` },
              { icon: '⏱',  label: exercise.duration },
            ].map(({ icon, label }) => (
              <div key={label} style={{
                background:   'rgba(255,255,255,0.08)',
                border:       `1px solid ${C.border}`,
                borderRadius: '10px',
                padding:      '8px 16px',
                color:        C.textSecondary,
                fontSize:     14,           // ↑ 原本未設定
                fontWeight:   600,
              }}>
                {icon} {label}
              </div>
            ))}
                    </div>

                    {/* 步驟說明（逐步顯示） */}
                    <div style={{ marginBottom: 20 }}>
                        {exercise.instructions.map((step, idx) => (
                            <span
                                key={idx}
                                onClick={() => setCurrentStep(idx)}
                                style={{
                  display:      'flex',
                  gap:          12,
                  padding:      '12px 14px',   // ↑ 原本 8px 10px
                  borderRadius: '10px',
                  background:   currentStep === idx ? C.stepActiveBg : 'transparent',
                  border:       `1px solid ${currentStep === idx ? C.borderActive : 'transparent'}`,
                                    cursor:     'pointer',
                  transition:   'all 0.2s',
                  marginBottom: 6,
                  alignItems:   'flex-start',
                                }}
                            >
                                {/* 步驟序號 */}
                <span style={{
                  background:   currentStep === idx ? C.accent : 'rgba(255,255,255,0.15)',
                  color:        currentStep === idx ? '#000000' : C.textSecondary,
                                    borderRadius: '50%',
                  width:        28,            // ↑ 原本未設定（約22）
                  height:       28,
                  minWidth:     28,
                                    display:      'flex',
                                    alignItems:   'center',
                                    justifyContent: 'center',
                  fontSize:     13,
                  fontWeight:   700,
                                }}>
                                    {idx + 1}
                                </span>
                                <p style={{
                                    color:      currentStep === idx ? C.textPrimary : C.textSecondary,
                                    fontSize:   15,              // ↑ 原本未設定（約13）
                                    lineHeight: 1.7,
                                    fontWeight: currentStep === idx ? 600 : 400,
                                }}>
                                    {step}
                                </p>
                            </span>
                        ))}
                    </div>

                    {/* 計時器 */}
                    <div style={{
            background:   'rgba(255,255,255,0.05)',
            border:       `1px solid ${C.border}`,
            borderRadius: '14px',
            padding:      '20px',
            display:      'flex',
            alignItems:   'center',
            gap:          20,
            flexWrap:     'wrap',
                    }}>
                        {/* 圓形進度 */}
                        <div style={{
                            position:   'relative',
                            width:       88, height: 88,
                            margin:      '0 auto 12px',
                        }}>
                            <svg width={88} height={88} style={{ transform: 'rotate(-90deg)' }}>
                                <circle cx={44} cy={44} r={radius}
                                        fill="none" stroke={C.timerTrack} strokeWidth={6} />
                                <circle
                                    cx={44} cy={44} r={radius}
                                    fill="none"
                                    stroke={timer.remaining > 0 ? C.accent : C.success}
                                    strokeWidth={6}
                                    strokeDasharray={circumference}
                                    strokeDashoffset={circumference * (1 - timer.progress / 100)}
                                    strokeLinecap="round"
                                    style={{ transition: 'stroke-dashoffset 1s linear' }}
                                />
                            </svg>
                            <div style={{
                position:   'absolute',
                inset:       0,
                display:    'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color:      C.textPrimary,
                fontSize:   18,              // ↑ 原本未設定
                fontWeight: 700,
                fontVariantNumeric: 'tabular-nums',
                            }}>
                {/*<span style={{ fontSize: 18, fontWeight: 700, color: '#fff' }}>*/}
                  {timer.display}
                {/*</span>*/}
                            </div>
                        </div>

                        {/* 計時器控制按鈕 */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: 1  }}>
                            {!timer.isRunning ? (
                                <button
                                    onClick={timer.start}
                                    // disabled={timer.remaining === 0}
                                    style={{
                                        padding:      '14px 20px',   // ↑ 原本未設定
                                        background:   timer.remaining === 0
                                            ? C.successBg
                                            : 'linear-gradient(135deg, #0066cc, #60C8FF)',
                                        border:       `2px solid ${timer.remaining === 0 ? C.success : 'transparent'}`,
                                        borderRadius: '10px',
                                        color:        '#FFFFFF',
                                        fontSize:     16,            // ↑ 原本未設定
                                        fontWeight:   700,
                                        cursor:       'pointer',
                                    }}
                                >
                                    {timer.remaining === 0 ? '✓ 完成！' : '▶ 開始計時'}
                                </button>
                            ) : (
                                <button
                                    onClick={timer.pause}
                                    style={{
                    padding:      '14px 20px',
                    background:   C.warningBg,
                    border:       `2px solid ${C.warning}`,
                    borderRadius: '10px',
                    color:        C.warning,
                    fontSize:     16,
                    fontWeight:   700,
                                        cursor:       'pointer',
                                    }}
                                >
                                    ⏸ 暫停
                                </button>
                            )}
                            <button
                                onClick={timer.reset}
                                style={{
                  padding:      '10px 20px',
                  background:   'rgba(255,255,255,0.07)',
                  border:       `1px solid ${C.border}`,
                  borderRadius: '10px',
                  color:        C.textSecondary,
                  fontSize:     14,
                  fontWeight:   600,
                                    cursor:       'pointer',
                                }}
                            >
                                ↺ 重設
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// ── 主元件 ───────────────────────────────────────────────
interface ExerciseGuideProps {
    /** 若提供，只顯示針對此問題的運動；否則顯示全部 */
    filterByIssue?: PostureIssueType | null;
}

const ExerciseGuide: FC<ExerciseGuideProps> = ({ filterByIssue }) => {
    const [filter, setFilter] = useState<PostureIssueType | 'all'>(
        filterByIssue ?? 'all'
    );

    const filteredExercises = filter === 'all'
        ? EXERCISES
        : EXERCISES.filter(e => e.targetIssue === filter);

    const workstationExercises  = filteredExercises.filter(e => e.canDoAtWorkstation);
    const extendedExercises     = filteredExercises.filter(e => !e.canDoAtWorkstation);

    return (
        <div className="exercise-guide" style={{ color: C.textPrimary }}>
            <h2 style={{ marginBottom: 6 }}>🧘 矯正運動指引</h2>
            <p style={{ color: C.textSecondary, fontSize: 13, marginBottom: 20 }}>
                依照 Certified Posture Specialist 建議設計，每日練習可有效改善姿勢問題
            </p>

            {/* 篩選按鈕 */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
                <button
                    onClick={() => setFilter('all')}
                    style={{
            padding:      '10px 20px',   // ↑ 原本 6px 16px
            borderRadius: '24px',
            border:       `2px solid ${filter === 'all' ? C.borderActive : C.border}`,
            background:   filter === 'all' ? C.accentBg : 'transparent',
            color:        filter === 'all' ? C.accent : C.textSecondary,
                        cursor:       'pointer',
            fontSize:     14,            // ↑ 原本 12
            fontWeight:   filter === 'all' ? 700 : 400,
            transition:   'all 0.2s',
                    }}
                >
                    全部
                </button>

        {/* 問題類別按鈕 */}
                {EXERCISES.map(e => e.targetIssue)
                    .filter((v, i, a) => a.indexOf(v) === i)  // 去重
                    .map(issueType => {
                        const labels: Record<PostureIssueType, string> = {
                            HEAD_FORWARD:    '頭部前傾',
                            SHOULDER_UNEVEN: '肩膀',
                            SPINE_TILT:      '脊椎',
                            ROUNDED_BACK:    '圓背',
                            PELVIC_TILT:     '骨盆',
                            KNEE_LOCKED:     '膝蓋',
                            WEIGHT_SHIFT:    '重心',
                        };
                        return (
                            <button
                                key={issueType}
                                onClick={() => setFilter(issueType)}
                                style={{
                padding:      '10px 20px',
                borderRadius: '24px',
                border:       `2px solid ${filter === issueType ? C.borderActive : C.border}`,
                background:   filter === issueType ? C.accentBg : 'transparent',
                color:        filter === issueType ? C.accent : C.textSecondary,
                                    cursor:       'pointer',
                fontSize:     14,
                fontWeight:   filter === issueType ? 700 : 400,
                transition:   'all 0.2s',
                                }}
                            >
                                {labels[issueType]}
                            </button>
                        );
                    })
                }
            </div>

            {/* 崗位可做的運動 */}
            {workstationExercises.length > 0 && (
                <section style={{ marginBottom: 32 }}>
                    <h3 style={{
            color:        C.success,
            fontSize:     16,
            fontWeight:   700,
            marginBottom: 14,
                    }}>
                        <span>🏪</span> 工作崗位上可進行
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {workstationExercises.map(e => (
                            <ExerciseCard key={e.id} exercise={e} />
                        ))}
                    </div>
                </section>
            )}

            {/* 休息時間進行的運動 */}
            {extendedExercises.length > 0 && (
                <section>
                    <h3 style={{
            color:        C.warning,
            fontSize:     16,
            fontWeight:   700,
            marginBottom: 14,
                    }}>
                        <span>⏸</span> 休息或下班後進行
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {extendedExercises.map(e => (
                            <ExerciseCard key={e.id} exercise={e} />
                        ))}
                    </div>
                </section>
            )}

            {filteredExercises.length === 0 && (
                <p style={{
          color:     C.textMuted,
          fontSize:  16,
          textAlign: 'center',
          padding:   '40px 0',
                }}>
                    此類別暫無對應運動
                </p>
            )}
        </div>
    );
};

export default ExerciseGuide;
